import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Badge, IconButton, Kbd, Logo, Search, ThemeToggle, Tooltip } from '@/components/ui';
import { cn } from '@/lib/cn';
import { useMarket } from '@/providers/market';
import { useCommandPalette } from './AppShell';
import { ChainSwitcher } from './ChainSwitcher';
import { WalletButton } from './WalletButton';

const NAV = [
  { to: '/', label: 'Explore' },
  { to: '/launch', label: 'Launch' },
  { to: '/portfolio', label: 'Portfolio' },
] as const;

/** A token page is a leaf of Explore, so Explore stays lit while you are on one. */
function isActive(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/' || pathname.startsWith('/token');
  return pathname === to || pathname.startsWith(`${to}/`);
}

export function Header() {
  const { pathname } = useLocation();
  const { openPalette } = useCommandPalette();
  const { isDemo } = useMarket();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={cn(
        'glass sticky top-0 z-40 h-header border-b transition-[border-color,box-shadow] duration-200 ease-swift',
        scrolled ? 'border-line-strong shadow-card' : 'border-line',
      )}
    >
      <div className="shell flex h-full items-center gap-2 sm:gap-3">
        {/* ── Brand ─────────────────────────────────────────────────────── */}
        <Link
          to="/"
          className="group flex shrink-0 items-center gap-2 rounded-md outline-none"
          aria-label="bull.markets — home"
        >
          <Logo size={26} className="text-bull transition-transform duration-300 ease-swift group-hover:scale-105" />
          <span className="font-display text-base font-extrabold leading-none tracking-tight">
            bull<span className="hidden text-muted sm:inline">.markets</span>
          </span>
        </Link>

        {/* ── Primary nav (lg+) ─────────────────────────────────────────────
             Held at lg, not md: measured at 768 the row wants 878px with a wallet
             attached (nav 228 + controls 458 + brand 124), which scrolls a
             portrait iPad sideways. Below lg the bottom tab bar carries these. */}
        <nav aria-label="Primary" className="ml-2 hidden h-full items-center lg:flex">
          {NAV.map((item) => {
            const active = isActive(pathname, item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'relative flex h-full items-center px-3 text-sm font-medium transition-colors duration-200 ease-swift',
                  active ? 'text-fg' : 'text-muted hover:text-fg',
                )}
              >
                {item.label}
                {active && (
                  <span
                    aria-hidden="true"
                    className="animate-fade-in absolute inset-x-3 bottom-0 h-[2px] rounded-full bg-bull"
                  />
                )}
              </Link>
            );
          })}
        </nav>

        <div className="flex-1" />

        {/* ── Right cluster ─────────────────────────────────────────────── */}
        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          {/* Synthesised numbers must always announce themselves. Never hide this. */}
          {isDemo && (
            <Tooltip content="Every number on screen is synthesised for design preview — no chain reads, no live market, no funds involved.">
              <span className="inline-flex cursor-help items-center">
                <Badge variant="warn" size="sm" dot>
                  Demo data
                </Badge>
              </span>
            </Tooltip>
          )}

          {/* Search: full pill at xl, icon button from sm to xl. The pill costs 174px
              against the icon's 28 and at 1024 the connected row had no room for it.
              Below sm the bottom tab bar already carries a Search tab. */}
          <button
            type="button"
            onClick={openPalette}
            className="hidden h-9 items-center gap-2 rounded-md border border-line bg-elevated px-2.5 text-sm text-subtle transition-colors duration-200 ease-swift hover:border-line-strong hover:text-muted xl:flex"
          >
            <Search size={15} aria-hidden="true" />
            <span>Search tokens</span>
            <Kbd>⌘K</Kbd>
          </button>
          <span className="hidden sm:inline-flex xl:hidden">
            <IconButton label="Search tokens" size="sm" variant="ghost" onClick={openPalette}>
              <Search size={17} />
            </IconButton>
          </span>

          <ChainSwitcher />

          <span className="hidden sm:inline-flex">
            <ThemeToggle />
          </span>

          <WalletButton />
        </div>
      </div>
    </header>
  );
}

export default Header;
